// copied from @confluence/graphql/src/links/AGGWebSocketLink/getBackOffDelay.ts. Once we move to Relay, we can remove the file from that package.

import { calculateBackOffRange } from './calculateBackOffRange.ts';
/**
 * Picks a random number between the start and end values.
 *
 * @param start lower bound
 * @param end upper bound
 *
 * @returns The number between the start and end values.
 *
 * @example
 * const number = randomNumberBetween(1, 10);
 * // number = 5
 *
 */
export function randomNumberBetween(start: number, end: number): number {
	const spread = end - start;
	return Math.floor(start + Math.random() * spread);
}

/**
 * Pick a random number between the range and subtract the time already spent waiting.
 * Delay will be 0 if the time spent is greater than the delay.
 *
 * @param {[number, number]} range - The range of the back-off delay
 * @param {number} [timeSpent=0] - The time already spent waiting
 *
 * @returns {number} Remaining back-off delay in milliseconds
 *
 * @example
 * const delay = getBackOffDelay({ range: [800, 1800] }, 1000);
 * // delay = 432
 * const delay = getBackOffDelay({ range: [800, 1800] }, 2000);
 * // delay = 0
 * const delay = getBackOffDelay({ range: [800, 1800] });
 * // delay = 1234
 */
export const getBackOffDelay = ({
	range,
	timeSpent = 0,
}: {
	range: [number, number];
	timeSpent?: number;
}): number => {
	const [lowerBound, upperBound] = range;
	const delay = randomNumberBetween(lowerBound, upperBound);
	return Math.max(delay - timeSpent, 0);
};

export const getDelayRangeFromGraphqlWebsocketState = async (
	retries: number,
): Promise<[number, number]> => {
	/**
	 * We're overriding the status multipliers here to make the reconnection
	 * back-off less aggressive. To reduce client
	 * reconnect load on graphql-subscriptions.
	 * The default status multipliers are: { active: 1, idle: 10 }.
	 */

	const activeMultiplier = 1;

	let status: 'active' | 'idle' = 'idle';
	if (typeof window !== 'undefined') {
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		status = document.visibilityState === 'visible' ? 'active' : 'idle';
	} else {
		status = 'idle';
	}

	return calculateBackOffRange({
		status,
		attemptsCount: retries,

		statusMultipliersOverride: {
			active: activeMultiplier,
			idle: 10,
		},
	});
};
